import { ColumnsType } from 'antd/es/table'
import { SortOrder } from 'antd/es/table/interface'
import { Sorter } from 'components/Sorter/Sorter'
import { Dropdown } from 'components/Dropdown'
import { DescriptionCell } from 'components/DescriptionCell/DescriptionCell'
import { AddressCell } from 'components/AddressCell/AddressCell'
import { Status, HOSPITAL_STATUS_TO_CLASSNAME } from 'components/Status'
import { fetchDepartments } from 'pages/CRUDUser/api'
import { fullFormatPhoneNumber } from 'helper/common'
import { HOSPITAL_STATUSES_OPTIONS } from 'constants/common'
import { ReactComponent as FilterApplied16 } from 'assets/svg/FilterApplied16.svg'
import { ReactComponent as Filter16 } from 'assets/svg/Filter16.svg'
import {
  THospital,
  THospitalsColumnsFunc,
  THospitalsFilters,
  THospitalsFiltersFunc
} from './types'
import LinkAccount from '../../../../HealthSystemDetails/HospitalsTable/LinkAccount/LinkAccount'
import { HospitalMenu } from './Actions/Menu'
import { Link } from '../../../../../components/Link'
import { routes } from '../../../../../router'
import {
  ability,
  ACTIONS,
  ROLES,
  SUBJECTS,
  SubjectWithAttributes
} from 'features/Permission'
import { subject } from '@casl/ability'

export const columns = ({
  applyFilters,
  filters,
  hsId,
  communityId,
  setOpenArchivePopup,
  handleSelectRow,
  setOpenActivatePopup,
  canEditHS,
  user,
  canViewBankEnrolledColumn,
  canViewDepartments,
  outsideCommunityMode
}: THospitalsColumnsFunc): ColumnsType<THospital> => {
  const hospitalDetailsPath = (hospitalId: string) =>
    outsideCommunityMode
      ? `${routes.myCommunity}${routes.myOrganization}/hospitals/${hospitalId}`
      : `${routes.myCommunity}${routes.myOrganization}/health_systems/${hsId}/hospitals/${hospitalId}`
  const columns: ColumnsType<THospital> = [
    {
      dataIndex: 'name',
      key: 'name',
      width: 200,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<THospital>
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Hospital
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false,
      render: (_, { uuid, name }) => (
        <Link to={hospitalDetailsPath(uuid)}>{name}</Link>
      )
    },
    {
      dataIndex: 'status',
      key: 'status',
      width: 130,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<THospital>
          dataIndex="status"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Status
        </Sorter>
      ),
      filterDropdown: (props) => (
        <Dropdown.Filter<THospitalsFilters, THospitalsFiltersFunc, unknown>
          items={HOSPITAL_STATUSES_OPTIONS}
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="statuses"
          {...props}
        />
      ),
      render: (_, { status }) => (
        <Status.Default className={HOSPITAL_STATUS_TO_CLASSNAME[status]}>
          {status}
        </Status.Default>
      ),
      filterIcon: filters?.statuses?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    }
  ]
  if (canViewBankEnrolledColumn) {
    columns.push({
      dataIndex: 'business_details',
      key: 'business_details',
      width: 192,
      title: 'Bank account enrolled',
      render: () => <LinkAccount />
    })
  }
  columns.push(
    {
      dataIndex: 'phone_number',
      key: 'phone_number',
      width: 200,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<THospital>
          dataIndex="phone_number"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Phone number
        </Sorter>
      ),
      render: (_, { phone_number }) =>
        phone_number ? fullFormatPhoneNumber(phone_number) : '-',
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'address',
      key: 'address',
      width: 240,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<THospital>
          dataIndex="address"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Address
        </Sorter>
      ),
      render: (_, { address }) => <AddressCell address={address} />,
      sorter: true,
      showSorterTooltip: false
    }
  )

  if (canViewDepartments) {
    columns.push({
      dataIndex: 'departments',
      key: 'departments',
      width: 240,
      title: 'Departments',
      filterDropdown: (props) => (
        <Dropdown.Filter<THospitalsFilters, THospitalsFiltersFunc, unknown>
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={() =>
            fetchDepartments(communityId, hsId, undefined, outsideCommunityMode)
          }
          field="departments"
          asyncSearch
          {...props}
        />
      ),
      render: (_, { departments }) => {
        const text = departments?.map((i) => i.name).join(', ')
        return <DescriptionCell content={text} text={text} />
      },
      filterIcon: filters?.departments?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      showSorterTooltip: false
    })
  }

  if (canEditHS) {
    columns.push({
      title: '',
      dataIndex: 'action',
      key: 'action',
      width: 40,
      fixed: 'right',
      render: (_, row) => {
        const canEditCurrentHospital =
          ability.can(ACTIONS.CRUD, SUBJECTS.HOSPITALS_IN_HS_DETAILS) ||
          ability.can(
            ACTIONS.EDIT,
            subject(SUBJECTS.HOSPITALS_IN_HS_DETAILS, {
              uuid:
                user.role === ROLES.HOSPITAL_ADMIN
                  ? row.uuid
                  : user.health_system
            } as unknown as SubjectWithAttributes)
          )

        return (
          canEditCurrentHospital && (
            <HospitalMenu
              row={row}
              hsId={hsId}
              outsideCommunityMode={outsideCommunityMode}
              handleSelectRow={handleSelectRow}
              setOpenArchivePopup={setOpenArchivePopup}
              setOpenActivatePopup={setOpenActivatePopup}
            />
          )
        )
      }
    })
  }
  return columns
}
